<script setup lang="ts">
import { computed, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { IconCheck, IconChevronDown, IconSwitchHorizontal } from '@tabler/icons-vue'
import { Dropdown, DropdownItem } from '@ui'
import { landingPage, switchTarget } from '@/lib/intelligence-pages'
import type { WorkspaceAccess } from '@/types/api'

/**
 * The right of the Intelligence header (P1 §4): which workspace you are in,
 * with the others you can enter, and `Switch to Operations`, which opens the
 * same workspace in ElfieCare (P1 §3.2). Intelligence settings are at the
 * foot of the left menu (DECISIONS.md #43). Desktop only: below 1024px the
 * kit's header ignores taps on its right-hand side, so the phone menu carries
 * both (AppShell).
 */
const props = defineProps<{
  access: WorkspaceAccess
  /** Every workspace this person has, as the API lists them. */
  workspaces: WorkspaceAccess[]
}>()

const { t } = useI18n()
const route = useRoute()
const router = useRouter()

/** Only the workspaces there is a page to open in. */
const enterable = computed(() => props.workspaces.filter((workspace) => landingPage(workspace) !== null))

const switchTo = (workspace: WorkspaceAccess): void => {
  if (workspace.workspaceId === props.access.workspaceId) return
  const page = switchTarget(workspace, route.name)
  if (page) void router.push({ name: page, params: { workspaceId: workspace.workspaceId } })
}

/** Read out with the disabled control, which a keyboard can reach; a pointer also gets it as the tooltip. */
const unsetReasonId = useId()
</script>

<template>
  <div class="intel-bar" data-test="intelligence-header">
    <Dropdown placement="bottom-end" :content-max-height="320">
      <!-- Named after what it shows, then what it does (WCAG 2.5.3). -->
      <button
        type="button"
        class="intel-bar__pill"
        data-test="workspace-switcher"
        :aria-label="t('intelligence.workspaceSwitcherCurrent', { name: access.name })"
      >
        <span class="intel-bar__name">{{ access.name }}</span>
        <IconChevronDown :size="16" class="intel-bar__muted" aria-hidden="true" />
      </button>
      <template #dropdown>
        <DropdownItem
          v-for="workspace in enterable"
          :key="workspace.workspaceId"
          :data-test="`switch-to-${workspace.workspaceId}`"
          @click="switchTo(workspace)"
        >
          <span class="intel-bar__option">
            <span>{{ workspace.name }}</span>
            <IconCheck v-if="workspace.workspaceId === access.workspaceId" :size="16" :aria-label="t('intelligence.currentWorkspace')" />
          </span>
        </DropdownItem>
      </template>
    </Dropdown>

    <!-- A link to another web product, so it is one: it opens where ElfieCare keeps this workspace. -->
    <a
      v-if="access.operationsUrl"
      :href="access.operationsUrl"
      class="intel-bar__pill intel-bar__operations"
      data-test="switch-to-operations"
    >
      <IconSwitchHorizontal :size="16" aria-hidden="true" />
      <span>{{ t('intelligence.switchToOperations') }}</span>
    </a>
    <span
      v-else
      class="intel-bar__pill intel-bar__operations is-disabled"
      role="link"
      tabindex="0"
      aria-disabled="true"
      :aria-describedby="unsetReasonId"
      :title="t('intelligence.switchToOperationsUnset')"
      data-test="switch-to-operations"
    >
      <IconSwitchHorizontal :size="16" aria-hidden="true" />
      <span>{{ t('intelligence.switchToOperations') }}</span>
      <span :id="unsetReasonId" hidden>{{ t('intelligence.switchToOperationsUnset') }}</span>
    </span>
  </div>
</template>

<style scoped>
.intel-bar {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-md);
}

/* The pill the account uses on the navy field, so the bar reads as one set of controls. */
.intel-bar__pill {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
  max-width: var(--width-xxs);
  padding: var(--spacing-sm) var(--spacing-lg);
  border: 1px solid var(--line-on-field);
  border-radius: var(--radius-full);
  background: var(--surface-field-raised);
  color: var(--fg-on-field);
  cursor: pointer;
  font-family: var(--font-family-primary);
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
  font-weight: var(--font-weight-semibold);
  text-decoration: none;
  white-space: nowrap;
}

.intel-bar__pill:hover,
.intel-bar__pill:focus-visible {
  border-color: var(--fg-on-field-muted);
}

.intel-bar__name {
  overflow: hidden;
  text-overflow: ellipsis;
}

.intel-bar__muted {
  flex-shrink: 0;
  color: var(--fg-on-field-muted);
}

/* The way back to Operations is the bar's one call to action: it carries the accent. */
.intel-bar__operations {
  border-color: var(--action-solid);
}

.intel-bar__operations.is-disabled {
  border-color: var(--line-on-field);
  color: var(--fg-on-field-muted);
  cursor: not-allowed;
}

.intel-bar__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-lg);
  width: 100%;
}
</style>
